import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, ChevronDownIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { PaymentScheduleForm } from "./PaymentScheduleForm";
import { MultiSelectProposals } from "@/components/ui/multi-select-proposals";

const projectSchema = z.object({
  name: z.string().min(1, "Project name is required"),
  description: z.string().optional(),
  client_id: z.string().min(1, "Client is required"),
  proposal_ids: z.array(z.string()).optional(),
  status: z.enum(["draft", "confirmed", "in_progress", "delivered"]),
  type: z.enum(["recurring", "adhoc"]),
  sold_for: z.string().optional(),
  start_date: z.date().optional(),
  close_date: z.date().optional(),
  promised_delivery_date: z.date().optional(),
  notes: z.string().optional(),
  arr: z.boolean().optional(),
  product_id: z.string().optional(),
});

type ProjectFormData = z.infer<typeof projectSchema>;

interface ProjectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: any;
  clients: any[];
  proposals: any[];
}

export function ProjectForm({ open, onOpenChange, project, clients, proposals }: ProjectFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentSchedule, setPaymentSchedule] = useState(() => {
    if (project?.payment_schedule) {
      return project.payment_schedule;
    }
    return { type: 'once_off' as const };
  });

  // Effect to update payment schedule when project changes
  useEffect(() => {
    if (project?.payment_schedule) {
      setPaymentSchedule(project.payment_schedule);
    } else if (!project) {
      setPaymentSchedule({ type: 'once_off' as const });
    }
  }, [project]);
  const queryClient = useQueryClient();

  // Fetch products for product selection
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, base_price")
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
  });

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: project?.name || "",
      description: project?.description || "",
      client_id: project?.client_id || "",
      proposal_ids: project?.proposal_ids || (project?.proposal_id ? [project.proposal_id] : []),
      status: project?.status || "confirmed",
      type: project?.type || "adhoc",
      sold_for: project?.sold_for?.toString() || "",
      start_date: project?.start_date ? new Date(project.start_date) : undefined,
      close_date: project?.close_date ? new Date(project.close_date) : undefined,
      promised_delivery_date: project?.promised_delivery_date ? new Date(project.promised_delivery_date) : undefined,
      notes: project?.notes || "",
      arr: project?.arr || false,
      product_id: project?.product_id || "",
    },
  });

  const watchedStatus = form.watch("status");
  const watchedProposalIds = form.watch("proposal_ids");
  const watchedClientId = form.watch("client_id");
  const watchedDescription = form.watch("description");
  const watchedProductId = form.watch("product_id");

  // Auto-fill project value when product is selected
  useEffect(() => {
    if (!project && watchedProductId) { // Only for new projects
      const selectedProduct = products?.find(p => p.id === watchedProductId);
      if (selectedProduct?.base_price) {
        form.setValue("sold_for", selectedProduct.base_price.toString());
      }
    }
  }, [watchedProductId, products, form, project]);

  // Effect to handle start date based on status - removed to allow manual date selection

  // Track if user has manually edited the name
  const [hasUserEditedName, setHasUserEditedName] = useState(false);

  // Effect to auto-fill project name and value when proposals are selected
  useEffect(() => {
    if (watchedProposalIds && watchedProposalIds.length > 0) {
      // Sum values from all selected proposals
      const totalValue = watchedProposalIds.reduce((sum, id) => {
        const proposal = proposals?.find(p => p.id === id);
        return sum + (proposal?.final_value || proposal?.value || 0);
      }, 0);
      if (totalValue > 0) {
        form.setValue("sold_for", totalValue.toString());
      }
      
      // Only auto-fill name if user hasn't manually edited it and it's a new project
      if (!project && !hasUserEditedName) {
        const currentName = form.getValues("name");
        // Only auto-fill if name is empty
        if (!currentName || currentName.trim() === "") {
          const firstProposal = proposals?.find(p => p.id === watchedProposalIds[0]);
          if (firstProposal?.title) {
            form.setValue("name", firstProposal.title);
          }
        }
      }
    }
  }, [watchedProposalIds, proposals, form, project, hasUserEditedName]);

  // Effect to clear proposals when client changes
  useEffect(() => {
    if (!project && watchedClientId && watchedProposalIds && watchedProposalIds.length > 0) {
      // Check if any selected proposal belongs to a different client
      const hasInvalidProposal = watchedProposalIds.some(id => {
        const proposal = proposals?.find(p => p.id === id);
        return proposal && proposal.client_id !== watchedClientId;
      });
      if (hasInvalidProposal) {
        form.setValue("proposal_ids", []);
        form.setValue("name", "");
      }
    }
  }, [watchedClientId, watchedProposalIds, proposals, form, project]);

  // Effect to sync description to notes
  useEffect(() => {
    if (!project) { // Only for new projects
      form.setValue("notes", watchedDescription || "");
    }
  }, [watchedDescription, form, project]);

  // Filter proposals by selected client
  const filteredProposals = watchedClientId 
    ? proposals?.filter(proposal => proposal.client_id === watchedClientId) 
    : proposals;

  const onSubmit = async (data: ProjectFormData) => {
    setIsLoading(true);
    try {
      const derivedTools = (data.proposal_ids || []).flatMap((id) => {
        const proposal = proposals?.find((p) => p.id === id);
        return Array.isArray(proposal?.tools) ? proposal.tools : [];
      });
      const uniqueTools = [...new Set(derivedTools)];

      const projectData = {
        ...data,
        sold_for: data.sold_for ? parseFloat(data.sold_for) : null,
        product_id: data.product_id || null,
        proposal_ids: data.proposal_ids && data.proposal_ids.length > 0 ? data.proposal_ids : null,
        // Keep proposal_id for backwards compatibility (first selected proposal)
        proposal_id: data.proposal_ids && data.proposal_ids.length > 0 ? data.proposal_ids[0] : null,
        // Sync tools from proposals (when proposals are linked); otherwise keep existing tools on edit
        tools: uniqueTools.length > 0 ? uniqueTools : (project?.tools ?? null),
        start_date: data.start_date ? format(data.start_date, 'yyyy-MM-dd') : null,
        close_date: data.close_date ? format(data.close_date, 'yyyy-MM-dd') : null,
        promised_delivery_date: data.promised_delivery_date ? format(data.promised_delivery_date, 'yyyy-MM-dd') : null,
        notes: data.notes || null,
        payment_schedule: paymentSchedule,
      };

      if (project) {
        // Update existing project
        const { error } = await supabase
          .from("projects")
          .update(projectData)
          .eq("id", project.id);

        if (error) throw error;
        toast.success("Project updated successfully");
      } else {
        // Create new project without org_id
        const { error } = await supabase
          .from("projects")
          .insert([projectData]);

        if (error) throw error;
        toast.success("Project created successfully");
      }

      queryClient.invalidateQueries({ queryKey: ["projects"] });
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{project ? "Edit Project" : "Create New Project"}</DialogTitle>
          <DialogDescription>
            {project ? "Update project details below." : "Fill in the details to create a new project."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Enter project name" 
                        {...field} 
                        onChange={(e) => {
                          field.onChange(e);
                          setHasUserEditedName(true);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="client_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a client" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {clients?.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter project description"
                      className="min-h-[80px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="delivered">Ended</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="recurring">Recurring</SelectItem>
                        <SelectItem value="adhoc">Ad Hoc</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="product_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product/Service (Optional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a product" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {products?.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name} {product.base_price && `(€${product.base_price})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sold_for"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project Value</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.01"
                        placeholder="0.00" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

               <FormField
                control={form.control}
                name="proposal_ids"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Related Proposals (Optional)</FormLabel>
                    <FormControl>
                      <MultiSelectProposals
                        proposals={filteredProposals || []}
                        selectedProposalIds={field.value || []}
                        onSelectionChange={field.onChange}
                        placeholder="Select proposals..."
                        clientFilter={watchedClientId}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Start Date</FormLabel>
                     <Popover>
                       <PopoverTrigger asChild>
                         <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                               )}
                           >
                             {field.value ? (
                               format(field.value, "PPP")
                             ) : (
                               <span>Pick a date</span>
                             )}
                             <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                           </Button>
                         </FormControl>
                       </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) =>
                              date < new Date("1900-01-01")
                            }
                            initialFocus
                            className="pointer-events-auto"
                          />
                       </PopoverContent>
                     </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="close_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>End Date</FormLabel>
                     <Popover>
                       <PopoverTrigger asChild>
                         <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                               )}
                           >
                             {field.value ? (
                               format(field.value, "PPP")
                             ) : (
                               <span>Pick a date</span>
                             )}
                             <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                           </Button>
                         </FormControl>
                       </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) =>
                              date < new Date("1900-01-01")
                            }
                            initialFocus
                            className="pointer-events-auto"
                          />
                       </PopoverContent>
                     </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="arr"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>ARR Project</FormLabel>
                    <FormControl>
                      <div className="flex items-center space-x-2 h-10">
                        <Switch
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                        />
                        <span className="text-sm text-muted-foreground">
                          {field.value ? "Yes" : "No"}
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="promised_delivery_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Promised Delivery</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date < new Date("1900-01-01")
                          }
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Payment Schedule Section */}
            <div className="space-y-4">
              <PaymentScheduleForm
                value={paymentSchedule}
                onChange={setPaymentSchedule}
                projectValue={form.watch("sold_for") ? parseFloat(form.watch("sold_for")) : undefined}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : project ? "Update Project" : "Create Project"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}